import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import SearchableSelect from "../components/SearchableSelect";
import ConfirmDialog from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";

/** Renders a dynamic custom field defined in Settings → Custom Fields. */
function CustomFieldInput({ def, value, onChange }) {
  const base = "h-11 px-3 bg-white border border-[#E5D8C4] text-sm w-full focus:outline-none focus:border-[#2D2622]";
  const testId = `custom-${def.key}`;
  if (def.field_type === "number") {
    return <input type="number" data-testid={testId} value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      className={`${base} font-mono2`} />;
  }
  if (def.field_type === "date") {
    return <input type="date" data-testid={testId} value={value || ""}
      onChange={(e) => onChange(e.target.value)} className={base} />;
  }
  if (def.field_type === "dropdown") {
    return (
      <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base}>
        <option value="">— select —</option>
        {(def.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (def.field_type === "checkbox") {
    return (
      <label className="inline-flex items-center gap-2 h-11">
        <input type="checkbox" data-testid={testId} checked={!!value}
          onChange={(e) => onChange(e.target.checked)} />
        <span className="text-sm text-[#5D5148]">Yes</span>
      </label>
    );
  }
  return <input type="text" data-testid={testId} value={value || ""}
    onChange={(e) => onChange(e.target.value)} className={base} />;
}

export default function StockOut() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [lots, setLots] = useState([]);
  const [racks, setRacks] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [openPOs, setOpenPOs] = useState([]);
  const [f, setF] = useState({
    category_id: "", color_id: "", size_id: "",
    lot_id: "", rack_no: "", quantity: "",
    po_no: "", warehouse_place: "",
    employee_name: user?.full_name || "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});

  useEffect(() => {
    api.get("/categories").then(r => setCats(r.data));
    api.get("/warehouses").then(r => setWarehouses(r.data));
    api.get("/purchase-orders", { params: { open_only: true } })
      .then(r => setOpenPOs(r.data)).catch(() => setOpenPOs([]));
    api.get("/custom-fields", { params: { form_scope: "stock_out" } })
      .then(r => setCustomFields((r.data || []).filter(fd => !fd.hidden)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!f.category_id) return;
    api.get("/colors", { params: { category_id: f.category_id } }).then(r => setColors(r.data));
    api.get("/sizes", { params: { category_id: f.category_id } }).then(r => setSizes(r.data));
  }, [f.category_id]);

  useEffect(() => {
    const params = {};
    if (f.category_id) params.category_id = f.category_id;
    if (f.color_id) params.color_id = f.color_id;
    if (f.size_id) params.size_id = f.size_id;
    if (Object.keys(params).length === 0) { setLots([]); return; }
    api.get("/lots", { params }).then(r => setLots(r.data));
  }, [f.category_id, f.color_id, f.size_id]);

  const selectedLot = useMemo(() => lots.find(l => l.id === f.lot_id), [lots, f.lot_id]);

  // Racks are scoped to the selected lot's warehouse so operators can't
  // reassign stock to a rack from a different warehouse by mistake.
  useEffect(() => {
    const wh = selectedLot?.warehouse_place;
    const params = wh ? { warehouse: wh } : {};
    api.get("/racks", { params }).then(r => setRacks(r.data));
  }, [selectedLot?.warehouse_place]);

  const tryConfirm = (e) => {
    e.preventDefault();
    if (!f.lot_id) { toast.error("Select a lot"); return; }
    if (!f.quantity || parseInt(f.quantity, 10) <= 0) { toast.error("Enter valid quantity"); return; }
    setConfirmOpen(true);
  };

  const doSubmit = async () => {
    setBusy(true);
    try {
      for (const cf of customFields) {
        if (cf.required && (customValues[cf.key] === undefined || customValues[cf.key] === "" || customValues[cf.key] === null)) {
          toast.error(`${cf.label} is required`); setBusy(false); return;
        }
      }
      await api.post("/stock-out", {
        lot_id: f.lot_id, quantity: parseInt(f.quantity, 10),
        po_no: f.po_no, rack_no: f.rack_no,
        warehouse_place: f.warehouse_place,
        employee_name: f.employee_name, notes: f.notes,
        custom_fields: customValues,
      });
      toast.success(`Dispatched ${f.quantity} pcs on PO ${f.po_no}`);
      setConfirmOpen(false);
      nav(`/lot/${f.lot_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const opt = (arr, valKey = "id", labelKey = "name") =>
    arr.map(x => ({ value: x[valKey], label: x[labelKey], sub: x.note }));

  return (
    <div className="space-y-8" data-testid="stock-out-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-2">Dispatch</div>
        <h1 className="font-display font-bold text-4xl tracking-tight text-[#2D2622]">Take stock out.</h1>
        <p className="text-[#5D5148] mt-2 text-sm max-w-xl">
          Pick the exact lot, confirm the PO, and we'll reduce stock the second you press dispatch.
        </p>
      </header>

      <form onSubmit={tryConfirm} className="space-y-6 max-w-5xl">
        <section className="bg-white border border-[#E5D8C4] p-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Find the lot</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Category">
              <SearchableSelect testId="out-category" value={f.category_id}
                onChange={(v) => setF({ ...f, category_id: v, color_id: "", size_id: "", lot_id: "" })}
                options={cats.map(c => ({ value: c.id, label: c.name }))}
                placeholder="Choose…" />
            </Field>
            <Field label="Colour">
              <SearchableSelect testId="out-color" value={f.color_id}
                onChange={(v) => setF({ ...f, color_id: v, lot_id: "" })}
                options={colors.map(c => ({ value: c.id, label: c.name }))}
                placeholder="Choose…" disabled={!f.category_id} />
            </Field>
            <Field label="Size">
              <SearchableSelect testId="out-size" value={f.size_id}
                onChange={(v) => setF({ ...f, size_id: v, lot_id: "" })}
                options={sizes.map(s => ({ value: s.id, label: s.label }))}
                placeholder="Choose…" disabled={!f.category_id} />
            </Field>
          </div>
        </section>

        {lots.length > 0 && (
          <section className="bg-white border border-[#E5D8C4] p-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-3">
              Available lots ({lots.length})
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {lots.map(l => (
                <button type="button" key={l.id}
                  onClick={() => setF({ ...f, lot_id: l.id, rack_no: l.rack_no })}
                  data-testid={`out-lot-${l.id}`}
                  className={`text-left p-4 border transition-colors ${f.lot_id===l.id?"border-[#2D2622] bg-[#2D2622] text-white":"bg-white border-[#E5D8C4] hover:border-[#2D2622]"}`}>
                  <div className="flex justify-between items-start">
                    <div className="font-mono2 font-semibold text-sm">{l.lot_no}</div>
                    <div className={`px-2 py-0.5 text-[10px] font-mono2 ${f.lot_id===l.id?"bg-white text-[#2D2622]":"bg-[#F3ECDF]"}`}>{l.rack_no}</div>
                  </div>
                  <div className="mt-2 text-sm">{l.color_name}</div>
                  <div className={`text-xs font-mono2 mt-1 ${f.lot_id===l.id?"text-[#C4A57B]":"text-[#8B6F47]"}`}>{l.size_label}</div>
                  <div className={`mt-3 text-[10px] uppercase tracking-[0.2em] ${f.lot_id===l.id?"text-[#C4A57B]":"text-[#8B6F47]"}`}>
                    Available: <span className={`font-mono2 font-bold ${f.lot_id===l.id?"text-white":"text-[#2D2622]"}`}>{l.quantity}</span> pcs
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        <section className="bg-white border border-[#E5D8C4] p-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Dispatch details</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Quantity">
              <input required data-testid="out-qty" type="number" min="1" max={selectedLot?.quantity || undefined}
                value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })}
                className="h-11 px-3 bg-white border border-[#E5D8C4] text-sm w-full font-mono2 focus:outline-none focus:border-[#2D2622]" />
            </Field>
            <Field label="Rack No.">
              <SearchableSelect testId="out-rack" value={f.rack_no}
                onChange={(v) => setF({ ...f, rack_no: v })}
                options={racks.map(r => ({ value: r.name, label: r.name }))}
                placeholder="Select rack…" />
            </Field>
            <Field label="P.O. No." hint={openPOs.length ? `${openPOs.length} open` : "Type PO or free-text"}>
              {openPOs.length > 0 ? (
                <SearchableSelect testId="out-po" value={f.po_no}
                  onChange={(v) => setF({ ...f, po_no: v })}
                  options={openPOs.map(p => ({ value: p.po_no, label: p.po_no,
                                               sub: p.billing_company_name }))}
                  placeholder="Select open PO…" />
              ) : (
                <input required data-testid="out-po" placeholder="e.g. PO-2044"
                  value={f.po_no} onChange={(e) => setF({ ...f, po_no: e.target.value })}
                  className="h-11 px-3 bg-white border border-[#E5D8C4] text-sm w-full font-mono2 focus:outline-none focus:border-[#2D2622]" />
              )}
            </Field>
            <Field label="Warehouse Place">
              <SearchableSelect testId="out-warehouse" value={f.warehouse_place}
                onChange={(v) => setF({ ...f, warehouse_place: v })}
                options={warehouses.map(x => ({ value: x.name, label: x.name, sub: x.note }))} placeholder="Select…" />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Employee Name">
              <input required data-testid="out-emp" placeholder="Full name"
                value={f.employee_name} onChange={(e) => setF({ ...f, employee_name: e.target.value })}
                className="h-11 px-3 bg-white border border-[#E5D8C4] text-sm w-full focus:outline-none focus:border-[#2D2622]" />
            </Field>
            <Field label="Notes (optional)">
              <input data-testid="out-notes" value={f.notes}
                onChange={(e) => setF({ ...f, notes: e.target.value })}
                className="h-11 px-3 bg-white border border-[#E5D8C4] text-sm w-full focus:outline-none focus:border-[#2D2622]" />
            </Field>
          </div>
          {customFields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-[#F3ECDF]">
              {customFields.map(cf => (
                <Field key={cf.id} label={`${cf.label}${cf.required ? " *" : ""}`}>
                  <CustomFieldInput def={cf}
                    value={customValues[cf.key]}
                    onChange={(v) => setCustomValues(x => ({ ...x, [cf.key]: v }))} />
                </Field>
              ))}
            </div>
          )}
        </section>

        <div className="flex justify-end">
          <button type="submit" disabled={busy || !f.lot_id} data-testid="out-submit"
            className="inline-flex items-center gap-2 h-12 px-6 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#5D4A2E] disabled:opacity-40">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <>Confirm dispatch <ArrowRight size={14}/></>}
          </button>
        </div>
      </form>

      <ConfirmDialog open={confirmOpen} onCancel={() => setConfirmOpen(false)}
        onConfirm={doSubmit} loading={busy} tone="danger"
        title="Confirm dispatch"
        message={<>
          Ship <b>{f.quantity || 0}</b> pcs of <b>{selectedLot?.color_name}</b> ({selectedLot?.size_label})
          from Rack <b>{f.rack_no}</b> under PO <b>{f.po_no || "—"}</b>. This will reduce stock immediately.
        </>}
        confirmLabel="Yes, dispatch now" />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex justify-between items-baseline">
        <label className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2 block">{label}</label>
        {hint && <span className="text-[10px] text-[#A8998A]">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
